import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updatePassword,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

import { authenticationQueryKey } from "../queries/authentication-query-keys";

import {
  buildTokenInfo,
  buildUserPayload,
  safeParseAuthOperation,
} from "./amplify.helpers";
import type {
  GetAuthMethodParameters,
  IsomorphicAuthObject,
} from "./amplify.types";

import { queryClient } from "@/utils/QueryClientProvider";

class IsomorphicAuth implements IsomorphicAuthObject {
  constructor() {
    Hub.listen("auth", (data) => {
      if (data.payload.event === "tokenRefresh") {
        queryClient.invalidateQueries({
          queryKey: authenticationQueryKey.cloudFrontAuth(),
        });
      }
    });

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
          userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        },
      },
    });
  }

  async updatePassword(...params: GetAuthMethodParameters<"updatePassword">) {
    return safeParseAuthOperation(async () => await updatePassword(...params));
  }

  configure(...params: GetAuthMethodParameters<"configure">) {
    return Amplify.configure(...params);
  }

  async confirmSignIn(...params: GetAuthMethodParameters<"confirmSignIn">) {
    return safeParseAuthOperation(async () => await confirmSignIn(...params));
  }

  async currentAuthenticatedUser(forceRefresh?: boolean) {
    return safeParseAuthOperation(async () => {
      const user = await getCurrentUser();
      const { tokens } = await fetchAuthSession({ forceRefresh });
      const idToken = tokens?.idToken;
      const accessToken = tokens?.accessToken;

      return {
        authenticatedUser: buildUserPayload({ user, tokens }),
        idToken: buildTokenInfo(idToken),
        accessToken: buildTokenInfo(accessToken),
      };
    });
  }

  async signIn(...params: GetAuthMethodParameters<"signIn">) {
    return safeParseAuthOperation(async () => await signIn(...params));
  }

  async signUp(...params: GetAuthMethodParameters<"signUp">) {
    return safeParseAuthOperation(async () => await signUp(...params));
  }

  async signOut() {
    return safeParseAuthOperation(async () => await signOut());
  }

  async resetPassword(...params: GetAuthMethodParameters<"resetPassword">) {
    return safeParseAuthOperation(async () => await resetPassword(...params));
  }

  async confirmResetPassword(
    ...params: GetAuthMethodParameters<"confirmResetPassword">
  ) {
    return safeParseAuthOperation(
      async () => await confirmResetPassword(...params),
    );
  }
}

export const Auth = new IsomorphicAuth();
